import React, { useState, useEffect } from "react";
import { Form, Button, Modal, Row, Col } from "react-bootstrap";
import Select from "react-select";
import axios from "axios";
import BASE_URL from "../config";

const authAxios = axios.create({
  baseURL: BASE_URL,
});

authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AdminDriverCreation = ({ show, onClose, onDriverCreated, driverToEdit }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    showrooms: [],
  });
  const [showroomOptions, setShowroomOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAllShowrooms = async () => {
      try {
        const res = await authAxios.get(`/showrooms`);
        const data = res.data?.data?.data || res.data?.data || [];
        setShowroomOptions(
          data.map((s) => ({
            value: s.id,
            label: s.name,
          }))
        );
      } catch (err) {
        console.error("❌ Error fetching showrooms:", err);
      }
    };
    fetchAllShowrooms();
  }, []);

  useEffect(() => {
    if (driverToEdit) {
      setFormData({
        name: driverToEdit.name || "",
        phone: driverToEdit.phone || "",
        email: driverToEdit.email || "",
        password: "",
        showrooms: (driverToEdit.showrooms || []).map(s => ({
          value: s.id,
          label: s.name,
        })),
      });
    } else {
      setFormData({
        name: "",
        phone: "",
        email: "",
        password: "",
        showrooms: [],
      });
    }
    setErrors({});
  }, [driverToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleShowroomChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      showrooms: selectedOptions || [],
    }));
    if (errors.showroom_id) {
      setErrors(prev => ({ ...prev, showroom_id: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const showroomIds = formData.showrooms.map(showroom => showroom.value);
    
    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      showroom_id: showroomIds,
      is_active: true,
      online: false,
      role: 'driver'
    };

    if (formData.password) {
      payload.password = formData.password;
    }

    try {
      let res;
      if (driverToEdit) {
        res = await authAxios.put(`/users/${driverToEdit.id}`, payload);
        alert("Driver updated successfully! 🎉");
      } else {
        res = await authAxios.post("/users", payload);
        alert("Driver created successfully! 🎉");
      }
      
      console.log("✅ Driver operation successful:", res.data);
      onDriverCreated();
      onClose();
    } catch (err) {
      console.error("❌ Error submitting driver form:", err);
      // ✅ Add a check for err.response before accessing its properties
      if (err.response) {
        // Handle server-side validation errors (e.g., status 422)
        console.error("Server responded with an error:", err.response.data);
        setErrors(err.response.data.errors || {});
        alert("Error: " + JSON.stringify(err.response.data.message || err.response.data.error || "An error occurred."));
      } else {
        // Handle network or other unexpected errors
        console.error("Network or unknown error:", err.message);
        alert("Driver submission failed: Network or unknown error.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{driverToEdit ? "Edit Driver" : "Create New Driver"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group controlId="name" className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter name"
                  isInvalid={!!errors.name}
                  required
                />
                <Form.Control.Feedback type="invalid">{errors.name?.[0]}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="phone" className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter Phone Number"
                  isInvalid={!!errors.phone}
                  required
                />
                <Form.Control.Feedback type="invalid">{errors.phone?.[0]}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group controlId="email" className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter Email"
                  isInvalid={!!errors.email}
                  required
                />
                <Form.Control.Feedback type="invalid">{errors.email?.[0]}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="password" className="mb-3">
                <Form.Label>Password {driverToEdit && <small className="text-muted">(Leave blank to keep existing)</small>}</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter a password"
                  isInvalid={!!errors.password}
                  required={!driverToEdit}
                />
                <Form.Control.Feedback type="invalid">{errors.password?.[0]}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group controlId="showrooms" className="mb-3">
            <Form.Label>Showrooms</Form.Label>
            <Select
              isMulti
              options={showroomOptions}
              onChange={handleShowroomChange}
              placeholder="Select showrooms"
              value={formData.showrooms}
              isInvalid={!!errors.showroom_id}
            />
            {errors.showroom_id && <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>{errors.showroom_id[0]}</div>}
          </Form.Group>
          <Button variant="primary" style={{ width: "100%" }} type="submit" disabled={loading}>
            {loading ? "Submitting..." : (driverToEdit ? "Update" : "Create")}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AdminDriverCreation;